import { TENANT_ADMIN, PLATFORM_OWNER } from "../rbac/roles";

/** Permission IAM indiquant un administrateur tenant (accès complet modules). */
export const IAM_TENANT_ADMIN_PERMISSION = "iam.user.manage";

/** Permission IAM indiquant un super-admin plateforme. */
export const IAM_PLATFORM_SUPERADMIN_PERMISSION = "platform.manage";

/** Tenant IAM réservé aux super-admins plateforme (seed `platform`). */
export const PLATFORM_TENANT_SLUG = "platform";

/** Super-admin plateforme : permission plateforme sur le tenant `platform` uniquement. */
export function isIamPlatformSuperAdmin(
  rawIamPermissions: string[],
  tenantSlug: string,
): boolean {
  return (
    tenantSlug === PLATFORM_TENANT_SLUG &&
    rawIamPermissions.includes(IAM_PLATFORM_SUPERADMIN_PERMISSION)
  );
}

/** Slug RBAC RH aligné sur les permissions IAM brutes (avant expansion). */
export function resolveIamRoleSlug(
  rawIamPermissions: string[],
  tenantSlug?: string,
): string {
  if (
    tenantSlug &&
    tenantSlug !== PLATFORM_TENANT_SLUG &&
    rawIamPermissions.includes(IAM_TENANT_ADMIN_PERMISSION)
  ) {
    return "TENANT_ADMIN";
  }

  if (rawIamPermissions.includes(IAM_PLATFORM_SUPERADMIN_PERMISSION)) {
    return "PLATFORM_OWNER";
  }

  if (rawIamPermissions.includes(IAM_TENANT_ADMIN_PERMISSION)) {
    return "TENANT_ADMIN";
  }

  if (
    rawIamPermissions.includes("hr.recruitment.manage") ||
    rawIamPermissions.includes("hr.recruitment.publish")
  ) {
    return "RECRUITER";
  }

  if (
    rawIamPermissions.includes("hr.leave.approve") ||
    rawIamPermissions.includes("hr.employee.create")
  ) {
    return "HR_MANAGER";
  }

  if (rawIamPermissions.includes("hr.payroll.run")) {
    return "PAYROLL_MANAGER";
  }

  if (rawIamPermissions.some((perm) => perm.startsWith("hr."))) {
    return "HR_OPERATOR";
  }

  const eduSlug = resolveEduRoleSlug(rawIamPermissions);
  if (eduSlug) return eduSlug;

  return "IAM_SYNCED";
}

/** Infère le rôle Edu affiché (sidebar, accueil) à partir des permissions IAM. */
export function resolveEduRoleSlug(rawIamPermissions: string[]): string | null {
  if (!rawIamPermissions.some((perm) => perm.startsWith("edu."))) {
    return null;
  }

  if (rawIamPermissions.includes("edu.portal.parent.read")) {
    return "EDU_PARENT";
  }
  if (rawIamPermissions.includes("edu.portal.student.read")) {
    return "EDU_STUDENT";
  }
  if (rawIamPermissions.includes("edu.platform.catalog.write")) {
    return "EDU_PLATFORM_ADMIN";
  }
  if (rawIamPermissions.includes("edu.direction.read")) {
    return "EDU_DIRECTOR";
  }
  if (rawIamPermissions.includes("edu.attendance.manage")) {
    return "EDU_ATTENDANCE_MANAGER";
  }
  if (rawIamPermissions.includes("edu.life-school.read")) {
    return "EDU_LIFE_SCHOOL";
  }
  if (
    rawIamPermissions.includes("edu.teacher.workspace") &&
    rawIamPermissions.includes("edu.grades.write")
  ) {
    return "EDU_TEACHER";
  }
  if (
    rawIamPermissions.includes("edu.enrollments.write") &&
    !rawIamPermissions.includes("edu.teacher.workspace")
  ) {
    return "EDU_SECRETARY";
  }
  if (rawIamPermissions.includes("edu.teacher.workspace")) {
    return "EDU_HOMEROOM";
  }

  return null;
}

/**
 * Aligne les permissions IAM (`hr.employee.read`) avec le RBAC RH (`employee.read`).
 * Les admins tenant IAM reçoivent l'équivalent local TENANT_ADMIN pour l'UI RH.
 */
export function expandIamPermissionsForRh(
  iamPermissions: string[],
  tenantSlug?: string,
): string[] {
  const keys = new Set<string>(iamPermissions);

  for (const perm of iamPermissions) {
    const hrMatch = /^hr\.(.+)$/.exec(perm);
    if (hrMatch) {
      keys.add(hrMatch[1]);
    }
  }

  if (iamPermissions.includes(IAM_TENANT_ADMIN_PERMISSION)) {
    for (const permission of TENANT_ADMIN.permissions) {
      keys.add(permission.key);
    }
  }

  if (
    tenantSlug === PLATFORM_TENANT_SLUG &&
    iamPermissions.includes(IAM_PLATFORM_SUPERADMIN_PERMISSION)
  ) {
    for (const permission of PLATFORM_OWNER.permissions) {
      keys.add(permission.key);
    }
  }

  return [...keys];
}
